import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { pusher } from '../App';
import AppConstants from '../constants/appConstants';
import { fetchLaunchWindowsConfig } from '../services/launchWindows';
import { parseLaunchWindowsList } from '../utils/launchWindows';

const LaunchWindowsContext = createContext({ days: [], loading: true, refetch: () => {} });

/**
 * Fonte única da config de janelas de subida de lotes.
 *
 * Faz um único fetch (`/auction-config/launch-windows`) e mantém uma única
 * assinatura no Pusher ('auction-config' / 'launch-windows-updated'),
 * disponibilizando os `days` para todos os consumidores (banner, listagem e
 * filtros) — evitando buscas/assinaturas duplicadas.
 */
export const LaunchWindowsProvider = ({ children }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchIdRef = useRef(0);

  const loadConfig = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);

    try {
      const data = await fetchLaunchWindowsConfig();
      if (fetchId !== fetchIdRef.current) {
        return;
      }
      setDays(Array.isArray(data) ? data : []);
    } catch (error) {
      if (fetchId !== fetchIdRef.current) {
        return;
      }
      console.warn('Erro ao carregar janelas de subida (config):', error);
      setDays([]);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadConfig();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [loadConfig]);

  useEffect(() => {
    const channel = pusher.subscribe(AppConstants.LaunchWindowsPusherChannel);

    const handler = payload => {
      if (payload && Array.isArray(payload.launchWindows)) {
        setDays(parseLaunchWindowsList(payload.launchWindows));
      } else {
        loadConfig();
      }
    };

    channel.bind(AppConstants.LaunchWindowsUpdatedEvent, handler);

    return () => {
      channel.unbind(AppConstants.LaunchWindowsUpdatedEvent, handler);
      pusher.unsubscribe(AppConstants.LaunchWindowsPusherChannel);
    };
  }, [loadConfig]);

  return (
    <LaunchWindowsContext.Provider value={{ days, loading, refetch: loadConfig }}>
      {children}
    </LaunchWindowsContext.Provider>
  );
};

LaunchWindowsProvider.propTypes = {
  children: PropTypes.node,
};

LaunchWindowsProvider.defaultProps = {
  children: null,
};

/** Consome a config compartilhada de janelas de subida de lotes: { days, loading, refetch }. */
export const useLaunchWindowsConfig = () => useContext(LaunchWindowsContext);
